import { Copy, Edit3, Eye } from 'react-feather'
import { TablePaginationConfig } from 'antd'
import { Moment } from 'moment'
import React, { useCallback, useMemo } from 'react'
import { SharedContent } from 'src/models/SharedContent'
import { sharedAppStateStore } from 'src/store/AppStateStore'
import {
  Anchor,
  ContainerDiv,
  DefaultButton,
  FjDropdown,
  FjTable,
  FjText,
  ThreeDotMenuButton,
} from 'src/components/Common'
import { Paths } from 'src/constants/navigation'
import { copyToClipboard } from 'src/utils/format'
import { Colors } from 'src/constants/colors'
import { sharedDataStore } from 'src/store/DataStore'
import { getFeedContentTypeName, LearningContentType } from 'src/utils/content'

type SharedContentTableProps = {
  loading?: boolean
  showContentTitle?: boolean
  selectedContentType?: LearningContentType
  existingLinks: SharedContent[]
  sharedContentSaved?: (sharedContent: SharedContent) => void
  paginationSetting?: TablePaginationConfig | false
}

export const SharedContentTable = ({
  loading,
  showContentTitle,
  selectedContentType = 'asset',
  existingLinks = [],
  sharedContentSaved,
  paginationSetting = false,
}: SharedContentTableProps) => {
  const handleClickSessionsView = useCallback(
    (sharedContent: SharedContent) => {
      const onCancel = () => (sharedAppStateStore.sharedContentSessionModalProps = undefined)
      sharedAppStateStore.sharedContentSessionModalProps = {
        onCancel: onCancel,
        onSuccess: onCancel,
        sharedContent,
        sharedContentSaved,
      }
    },
    [sharedContentSaved]
  )

  const handleAssetClick = useCallback((assetId: string) => {
    sharedAppStateStore.sharedLinkModalProps = undefined
    sharedAppStateStore.navigate(Paths.getClassroomPath({ assetId }))
  }, [])

  const columns = useMemo(() => {
    return [
      {
        title: `Link Name (${sharedDataStore.user.company.integratedCrmContentTitle()})`,
        key: 'title',
        render: (content: SharedContent) => (
          <>
            {content.title}
            {content.sfMetadata?.name && (
              <>
                {' ('}
                <Anchor href={Paths.getOpportunityPath({ id: content.sfMetadata.id })} target="_blank">
                  {content.sfMetadata.name}
                </Anchor>
                {')'}
              </>
            )}
          </>
        ),
      },
      showContentTitle
        ? {
            title: `${getFeedContentTypeName({ contentType: selectedContentType, upperCase: true })} Name`,
            key: 'assetTitle',
            render: (content: SharedContent) => (
              <Anchor fontWeight="semi-bold" onClick={() => handleAssetClick(content.learningContent.id)}>
                {content.learningContent.title}
              </Anchor>
            ),
          }
        : undefined,
      {
        title: 'Author',
        key: 'authorName',
        render: (content: SharedContent) => content.author.fullName,
      },
      {
        title: 'Expires',
        dataIndex: 'expiryDate',
        key: 'expiryDate',
        render: (expiryDate: Moment) => (expiryDate ? expiryDate.fromNow() : null),
      },
      {
        title: 'Views',
        dataIndex: 'viewCount',
        key: 'viewCount',
        onCell: (record) => ({ onClick: () => handleClickSessionsView(record) }),
        render: (viewCount) => <ContainerDiv cursor="pointer">{viewCount}</ContainerDiv>,
      },
      {
        title: 'View Time',
        key: 'durationViewed',
        onCell: (record) => ({ onClick: () => handleClickSessionsView(record) }),
        render: (content: SharedContent) => (
          <ContainerDiv cursor="pointer">{content.getFormattedDurationViewed()}</ContainerDiv>
        ),
      },
      {
        title: 'Downloads',
        dataIndex: 'downloadCount',
        key: 'downloadCount',
        onCell: (record) => ({ onClick: () => handleClickSessionsView(record) }),
        render: (downloadCount) => <ContainerDiv cursor="pointer">{downloadCount}</ContainerDiv>,
      },
      {
        title: 'Password',
        key: 'password',
        render: (sharedContent: SharedContent) =>
          sharedContent.password ? (
            <ContainerDiv display="flex" gap="2px" alignItems="center" justifyContent="space-between">
              ••••••
              <DefaultButton
                size="small"
                image={<Copy size={16} />}
                clicked={() =>
                  copyToClipboard(
                    sharedContent.password,
                    'Password copied to clipboard!',
                    `${sharedContent.title} Password`
                  )
                }
              />
            </ContainerDiv>
          ) : (
            <ContainerDiv>
              <FjText color={Colors.tapa}>-</FjText>
            </ContainerDiv>
          ),
      },
      {
        title: 'Action',
        key: 'action',
        render: (sharedContent: SharedContent) => {
          const onCancel = () => (sharedAppStateStore.editSharedContentModalProps = undefined)
          return (
            <FjDropdown
              menu={{
                items: [
                  {
                    key: 'edit',
                    label: 'Edit',
                    icon: <Edit3 size={20} color={Colors.cornflowerBlue} />,
                    onClick: () => {
                      sharedAppStateStore.editSharedContentModalProps = {
                        obj: sharedContent,
                        onSuccess: (sharedContent: SharedContent) => {
                          sharedContentSaved?.(sharedContent)
                          onCancel()
                        },
                        onCancel: onCancel,
                      }
                    },
                  },
                  {
                    key: 'copy-url',
                    label: 'Copy URL',
                    icon: <Copy size={20} color={Colors.cornflowerBlue} />,
                    onClick: () =>
                      copyToClipboard(sharedContent.getShareLink(), undefined, `Link to ${sharedContent.title}`),
                  },
                  sharedContent.password
                    ? {
                        key: 'copy-password',
                        label: 'Copy Password',
                        icon: <Copy size={20} color={Colors.cornflowerBlue} />,
                        onClick: () =>
                          copyToClipboard(
                            sharedContent.password,
                            'Password copied to clipboard!',
                            `${sharedContent.title} Password`
                          ),
                      }
                    : undefined,
                  {
                    key: 'view-sessions',
                    label: 'View Sessions',
                    icon: <Eye size={20} color={Colors.cornflowerBlue} />,
                    onClick: () => handleClickSessionsView(sharedContent),
                  },
                ],
              }}
              placement="bottomRight"
            >
              <ThreeDotMenuButton iconSize={20} />
            </FjDropdown>
          )
        },
      },
    ].filter(Boolean)
  }, [showContentTitle, selectedContentType, handleAssetClick, handleClickSessionsView, sharedContentSaved])

  return <FjTable loading={loading} columns={columns} dataSource={existingLinks} pagination={paginationSetting} />
}
